// Admin role tiers. Mirrors the DB enforcement in migration 079:
//   admin     — full access; the only role that can change roles
//   moderator — "Community & Events": users, events, chats, reports, feedback, images
//   analyst   — read-only analytics: dashboard, signups funnel, waitlists
// ('support' exists in the DB enum but is unused for now.)
// RLS is the real gate — this module only decides what the UI shows.

import type { UserRole } from '../types';

/** The standalone admin panel (admin/ in this repo). */
export const ADMIN_PANEL_URL = 'https://admin.lincc.live';

export type AdminSection =
  | 'dashboard'
  | 'users'
  | 'events'
  | 'reports'
  | 'feedback'
  | 'images'
  | 'signups'
  | 'waitlist'
  | 'businesses'
  | 'business-applications'
  | 'categories'
  | 'announcements'
  | 'push'
  | 'feature-flags'
  | 'audit-log';

export const STAFF_ROLES: UserRole[] = ['admin', 'moderator', 'analyst'];

const SECTION_ACCESS: Record<AdminSection, UserRole[]> = {
  dashboard: ['admin', 'moderator', 'analyst'],
  users: ['admin', 'moderator'],
  events: ['admin', 'moderator'],
  reports: ['admin', 'moderator'],
  feedback: ['admin', 'moderator'],
  images: ['admin', 'moderator'],
  signups: ['admin', 'analyst'],
  waitlist: ['admin', 'analyst'],
  businesses: ['admin'],
  'business-applications': ['admin'],
  categories: ['admin'],
  announcements: ['admin'],
  push: ['admin'],
  'feature-flags': ['admin'],
  'audit-log': ['admin'],
};

export const ROLE_LABELS: Record<UserRole, string> = {
  user: 'User',
  admin: 'Full Admin',
  moderator: 'Community & Events',
  analyst: 'Analytics',
  support: 'Support',
};

/** Roles assignable from the admin UI ('support' is dormant). */
export const ASSIGNABLE_ROLES: UserRole[] = ['user', 'analyst', 'moderator', 'admin'];

export function isStaffRole(role: UserRole | null | undefined): boolean {
  return !!role && STAFF_ROLES.includes(role);
}

export function isFullAdmin(role: UserRole | null | undefined): boolean {
  return role === 'admin';
}

export function canAccessSection(role: UserRole | null | undefined, section: AdminSection): boolean {
  return !!role && SECTION_ACCESS[section].includes(role);
}
